import { useCallback, useState, useEffect, useRef } from "react";
import { directUploadWithProgress, updateMediaAltText } from "@core/features/uploadFile";
import type { QuizUploadedMedia } from "@core/features/quiz";
import { ImageField, type UploadResult } from "@themes/pollinator/ui/ImageField";

export type AltTextState = {
  /** Current alt text value */
  value: string;
  /** Handler for alt text changes */
  onChange: (value: string) => void;
  /** Handler for alt text blur (triggers server persistence) */
  onBlur: () => void;
  /** Whether an image is currently uploaded */
  hasImage: boolean;
};

export type Props = {
  /** Field name for identification */
  name: string;
  /** Current value (array of media IDs - usually just one) */
  value?: number[];
  /** Change handler */
  onChange: (value: number[]) => void;
  /** Blur handler */
  onBlur: () => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Initial files from previously uploaded media */
  initialFiles?: QuizUploadedMedia[];
  /** Callback to receive alt text state for external rendering */
  onAltTextStateChange?: (state: AltTextState) => void;
  /** Test ID */
  "data-testid"?: string;
};

/**
 * Container for ImageField that integrates with quiz upload infrastructure.
 * Provides upload handler that uses S3 direct upload system.
 *
 * This container handles:
 * - S3 direct upload via presigned URLs
 * - Progress tracking
 * - MediaUpload record creation
 * - Alt text persistence
 *
 * @example
 * <ImageFieldContainer
 *   name="question-123"
 *   value={[1]}
 *   onChange={(ids) => console.log(ids)}
 *   onBlur={() => {}}
 *   initialFiles={initialMedia}
 * />
 */
export function ImageFieldContainer({
  name: _name,
  value: _value = [],
  onChange,
  onBlur,
  disabled = false,
  error = false,
  initialFiles = [],
  onAltTextStateChange,
  "data-testid": testId = "image-field-container",
}: Props) {
  // Get the current image from initial files or track uploaded image
  const currentFile = initialFiles?.[0];
  const currentFileId = currentFile?.id;
  const currentFileUrl = currentFile?.file;
  const currentFileAltText = currentFile?.alt_text;

  const [uploadedImage, setUploadedImage] = useState<UploadResult | null>(
    currentFile
      ? {
          url: currentFile.file,
          id: currentFile.id,
        }
      : null
  );
  const [altText, setAltText] = useState(currentFile?.alt_text || "");

  // Sync with initialFiles when they change (use primitive values for comparison)
  useEffect(() => {
    if (currentFileId && currentFileUrl) {
      setUploadedImage({
        url: currentFileUrl,
        id: currentFileId,
      });
      setAltText(currentFileAltText || "");
    }
  }, [currentFileId, currentFileUrl, currentFileAltText]);

  const handleUpload = useCallback(
    async (
      file: File,
      onProgress: (progress: number) => void
    ): Promise<UploadResult> => {
      const mediaObject = await directUploadWithProgress(
        file,
        { file_type: "image" },
        onProgress
      );

      return {
        url: mediaObject.file,
        id: mediaObject.media_id,
      };
    },
    []
  );

  const handleChange = useCallback(
    (result: UploadResult | null) => {
      setUploadedImage(result);
      onChange(result?.id ? [result.id] : []);
      // Trigger blur after change to ensure form validation runs
      onBlur();
    },
    [onChange, onBlur]
  );

  const handleAltTextChange = useCallback(
    (newAltText: string) => {
      setAltText(newAltText);
    },
    []
  );

  // Persist alt text to server when blur occurs
  const handleAltTextBlur = useCallback(async () => {
    const mediaId = uploadedImage?.id;
    if (mediaId && altText && altText.trim() !== "") {
      try {
        await updateMediaAltText(mediaId, altText);
      } catch (err) {
        console.error("Failed to update alt text:", err);
      }
    }
  }, [uploadedImage?.id, altText]);

  // Track if we've notified the parent about alt text state
  const hasNotifiedRef = useRef(false);
  const hasImage = !!uploadedImage?.url;

  // Notify parent of alt text state for external rendering
  useEffect(() => {
    if (onAltTextStateChange) {
      onAltTextStateChange({
        value: altText,
        onChange: handleAltTextChange,
        onBlur: handleAltTextBlur,
        hasImage,
      });
      hasNotifiedRef.current = true;
    }
  }, [onAltTextStateChange, altText, handleAltTextChange, handleAltTextBlur, hasImage]);

  return (
    <ImageField
      image={uploadedImage?.url}
      altText={altText}
      onUpload={handleUpload}
      onChange={handleChange}
      disabled={disabled}
      error={error}
      data-testid={testId}
    />
  );
}

export default ImageFieldContainer;
